'use client';

import { ITheme } from '@/src/lib/types/themeInterface';
import React, { FC } from 'react';
import Layout from '@/src/components/layout/Layout';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import AdvantageList from '@/src/components/sections/advantageList/AdvantageList';
import { advInfoFull } from '@/src/lib/staticObjects/advInfo';
import styles from './Theme.module.scss';
import Breadcrumbs from '@/src/components/ui/breadcrumbs/Breadcrumbs';
import AdsSection from '@/src/components/sections/adsSection/AdsSection';
import MainInfo from '@/src/components/sections/mainInfo/MainInfo';
import DemoCard from '@/src/components/ui/demoCard/DemoCard';
import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import FeaturesSection from '@/src/components/sections/featuresSection/FeaturesSection';
import FooterCTA from '@/src/components/sections/footerCTA/FooterCTA';

import ThemeStockist from './ThemeStockist';
import ThemePrompt from './ThemePrompt';
import HelpSection from '@/src/components/sections/helpSection/HelpSection';
import SubscribeSection from '@/src/components/sections/subscribeSection/SubscribeSection';
import classNames from 'classnames';

type TTheme = {
  theme: ITheme;
};

const Theme: FC<TTheme> = ({ theme }) => {
  const { ref: inViewRef1, inView: inView1 } = useInView({
    triggerOnce: true,
    rootMargin: `-250px 0px 0px`,
    threshold: 0,
  });

  const { ref: inViewRef2, inView: inView2 } = useInView({
    triggerOnce: true,
    rootMargin: `-250px 0px 0px`,
    threshold: 0,
  });

  const heading = 'Built for Unmatched Quality';
  const excerpt =
    'Designed following the highest standards, we aim to maximize your store’s success.';

  const isStockist = theme.slug === 'stockist';
  const isPrompt = theme.slug === 'prompt';

  return (
    <Layout className={isStockist ? 'theme-stockist' : isPrompt ? 'theme-prompt' : ''}>
      <Breadcrumbs />

      <MainInfo themeData={theme} />

      {isStockist ? (
        <ThemeStockist theme={theme} />
      ) : isPrompt ? (
        <ThemePrompt theme={theme} />
      ) : (
        <>
          <section ref={inViewRef1} className={styles.demos}>
            <Wrapper>
              <h2 className={`heading__2 ${styles.heading} ${inView1 && styles.animate__heading}`}>
                {theme.demoSectionTitle &&
                  createAnimationArr(theme.demoSectionTitle, styles.word__wrapper, 0, 0.01)}
              </h2>
              <p className={`excerpt ${styles.excerpt} ${inView1 && styles.animate__excerpt}`}>
                {theme.demoSectionDescription &&
                  createAnimationArr(theme.demoSectionDescription, styles.word__wrapper, 0.1, 0.01)}
              </p>
              <ul className={styles.themes__list}>
                {theme.demoList?.map((demo, i) => (
                  <li key={`${demo._key}-${demo.demoName}`}>
                    <DemoCard demoData={demo} index={i} theme={theme} isThemePage />
                  </li>
                ))}
              </ul>
            </Wrapper>
          </section>

          <AdsSection themeData={theme} />

          <FeaturesSection />

          <SubscribeSection themeSlug={theme.slug} colorScheme={'dark'} />

          <section ref={inViewRef2} className={styles.advantage__section}>
            <Wrapper>
              <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
                {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
              </h2>
              <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
                {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
              </p>
              <AdvantageList advList={advInfoFull} />
            </Wrapper>
          </section>

          <HelpSection themeSlug={theme.slug} documentationLink={theme.documentationLink} />

          <SubscribeSection
            className={classNames(styles['subscribeSection'], styles['subscribeSection--light'])}
            themeSlug={theme.slug}
            colorScheme={'grey'}
          />
        </>
      )}

      <FooterCTA theme={theme} />
    </Layout>
  );
};

export default Theme;
